import axios from 'axios';
import { apiBase, authToken } from '../api/apiBase';
import { PHASES_RETAINED_READ_TIMEOUT_MS } from './mackPhasesEvidence';

export const USD_CPI_ADOPTION_SCOPE = 'USD_CPI_RETAINED_HISTORY_60' as const;
export const USD_CPI_ADOPTION_CAPABILITY = 'PHASES_USD_CPI_RETAINED_HISTORY_ADOPTION' as const;
export interface PhasesUsdCpiAdoptionAccess {
  authenticated: boolean; owner_authorized: boolean; session_scope: string;
  session_class: 'OWNER' | 'AUTHENTICATED_NON_OWNER' | 'UNAUTHENTICATED';
  capability: typeof USD_CPI_ADOPTION_CAPABILITY;
}
export interface PhasesUsdCpiAdoptionInventory {
  sealed_sha256: string; total: number; missing: number;
  overlap_periods: string[]; unpublished_periods: string[];
  first_period: string; last_period: string;
}
export interface PhasesUsdCpiAdoptionOperation {
  request_id: string; state: string; requested_at: string;
  started_at?: string | null; ended_at?: string | null; adoption_at?: string | null;
  source_requests: number;
  returned?: number; admitted?: number; deduplicated?: number; revised?: number;
  admitted_periods?: string[];
  upstream_periods_reused_no_copy?: { reference_period: string; state: 'UPSTREAM_REUSED_NO_COPY';
    upstream_material_identities?: string[]; [key: string]: unknown }[];
  receipt_location: string; original_operation_id: string; original_claim_identity: string;
  source_response_references?: Record<string, unknown>[];
  transaction_reference?: unknown; intent_reference?: unknown; intent_state?: string | null;
  persistence_complete?: boolean | null;
  [key: string]: unknown;
}
export interface PhasesUsdCpiAdoptionStatus {
  success: true; schema: 'mack_phases_usd_cpi_history_adoption_v1'; scope: typeof USD_CPI_ADOPTION_SCOPE;
  access?: PhasesUsdCpiAdoptionAccess;
  can_submit: boolean; resident_available: boolean; authority_state: string;
  inventory_summary: PhasesUsdCpiAdoptionInventory | null;
  operation: PhasesUsdCpiAdoptionOperation | null;
  blocker: string | null;
  [key: string]: unknown;
}

const nonempty = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const count = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const optionalText = (value: unknown) => value === undefined || value === null || typeof value === 'string';
const period = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
const periods = (value: unknown): value is string[] => Array.isArray(value) && value.every(period) && new Set(value).size === value.length;
const states = new Set(['QUEUED', 'RUNNING', 'RECOVERING', 'PERSISTENCE_PENDING', 'COMPLETED', 'PARTIAL', 'FAILED', 'WITHHELD']);

export function usdCpiAdoptionOwnerAuthorized(status: Pick<PhasesUsdCpiAdoptionStatus, 'access'>): boolean {
  return status.access?.authenticated === true && status.access.owner_authorized === true &&
    status.access.session_class === 'OWNER' && ['FULL', 'TRADING'].includes(status.access.session_scope) &&
    status.access.capability === USD_CPI_ADOPTION_CAPABILITY;
}

export class PhasesUsdCpiAdoptionResponseError extends Error {
  constructor() { super('Retained USD CPI adoption response is invalid'); this.name = 'PhasesUsdCpiAdoptionResponseError'; }
}
export class PhasesUsdCpiAdoptionTimeoutError extends Error {
  constructor() { super('Retained USD CPI adoption request timed out'); this.name = 'PhasesUsdCpiAdoptionTimeoutError'; }
}

export function decodeUsdCpiAdoptionStatus(raw: unknown): PhasesUsdCpiAdoptionStatus {
  const p = raw as PhasesUsdCpiAdoptionStatus;
  const access = p?.access;
  const inventory = p?.inventory_summary;
  const operation = p?.operation;
  if (!p || p.success !== true || p.schema !== 'mack_phases_usd_cpi_history_adoption_v1' ||
      p.scope !== USD_CPI_ADOPTION_SCOPE || typeof p.can_submit !== 'boolean' ||
      typeof p.resident_available !== 'boolean' || !nonempty(p.authority_state) || !optionalText(p.blocker) ||
      (access !== undefined && (!access || typeof access.authenticated !== 'boolean' ||
        typeof access.owner_authorized !== 'boolean' || !nonempty(access.session_scope) ||
        !['OWNER', 'AUTHENTICATED_NON_OWNER', 'UNAUTHENTICATED'].includes(access.session_class) ||
        access.capability !== USD_CPI_ADOPTION_CAPABILITY || (access.owner_authorized && !usdCpiAdoptionOwnerAuthorized(p)))) ||
      (inventory !== null && (!inventory || !/^[a-f0-9]{64}$/.test(inventory.sealed_sha256) ||
        !count(inventory.total) || !count(inventory.missing) || inventory.missing > inventory.total ||
        !periods(inventory.overlap_periods) || !periods(inventory.unpublished_periods) ||
        !period(inventory.first_period) || !period(inventory.last_period) || inventory.first_period > inventory.last_period)) ||
      (operation !== null && (!operation || !nonempty(operation.request_id) || !states.has(operation.state) ||
        !nonempty(operation.requested_at) || !optionalText(operation.started_at) || !optionalText(operation.ended_at) ||
        !optionalText(operation.adoption_at) || operation.source_requests !== 0 ||
        !nonempty(operation.receipt_location) || !nonempty(operation.original_operation_id) || !nonempty(operation.original_claim_identity) ||
        ['returned', 'admitted', 'deduplicated', 'revised'].some(key => operation[key] !== undefined && !count(operation[key])) ||
        (operation.admitted_periods !== undefined && !periods(operation.admitted_periods)) ||
        (operation.upstream_periods_reused_no_copy !== undefined && (!Array.isArray(operation.upstream_periods_reused_no_copy) ||
          operation.upstream_periods_reused_no_copy.some(row => !row || !period(row.reference_period) || row.state !== 'UPSTREAM_REUSED_NO_COPY' ||
            (row.upstream_material_identities !== undefined && (!Array.isArray(row.upstream_material_identities) ||
              row.upstream_material_identities.some(identity => !nonempty(identity))))) ||
          new Set(operation.upstream_periods_reused_no_copy.map(row => row.reference_period)).size !== operation.upstream_periods_reused_no_copy.length)) ||
        !optionalText(operation.intent_state) ||
        (operation.persistence_complete !== undefined && operation.persistence_complete !== null && typeof operation.persistence_complete !== 'boolean') ||
        (operation.source_response_references !== undefined && (!Array.isArray(operation.source_response_references) ||
          operation.source_response_references.some(row => !row || typeof row !== 'object' || Array.isArray(row)))))) ||
      (p.can_submit && (p.authority_state !== 'VERIFIED' || !p.resident_available || operation !== null ||
        !inventory || inventory.total !== 60 || !usdCpiAdoptionOwnerAuthorized(p) || !!p.blocker))) {
    throw new PhasesUsdCpiAdoptionResponseError();
  }
  return p;
}

export async function readUsdCpiAdoption(token?: string | null, signal?: AbortSignal): Promise<PhasesUsdCpiAdoptionStatus> {
  const response = await axios.get(`${apiBase()}/mack/phases/usd-cpi-history-adoption`, {
    headers: { Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache' },
    signal, timeout: PHASES_RETAINED_READ_TIMEOUT_MS,
  });
  return decodeUsdCpiAdoptionStatus(response.data);
}

// Server-side sealed state resolves every source, period, claim and destination.
export async function submitUsdCpiAdoption(token?: string | null, signal?: AbortSignal): Promise<PhasesUsdCpiAdoptionStatus> {
  const response = await axios.post(`${apiBase()}/mack/phases/usd-cpi-history-adoption`, { scope: USD_CPI_ADOPTION_SCOPE }, {
    headers: { Authorization: `Bearer ${token || authToken()}` }, signal, timeout: PHASES_RETAINED_READ_TIMEOUT_MS,
  });
  return decodeUsdCpiAdoptionStatus(response.data);
}

export function usdCpiAdoptionFailure(error: unknown, submission = false): string {
  const suffix = submission ? ' Refresh USD CPI adoption status before submitting again.' : ' Refresh USD CPI adoption status to check again.';
  if (error instanceof PhasesUsdCpiAdoptionResponseError) return `The backend returned an invalid retained-history ${submission ? 'acknowledgement' : 'status'}.${suffix}`;
  if (error instanceof PhasesUsdCpiAdoptionTimeoutError || (axios.isAxiosError(error) && ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code || ''))) {
    return `The retained-history ${submission ? 'acknowledgement' : 'status read'} timed out. The server operation was not cancelled.${suffix}`;
  }
  const status = axios.isAxiosError(error) ? error.response?.status : null;
  if (status === 401) return 'Your session is unauthenticated or has expired. Sign in again to view retained USD CPI adoption.';
  if (status === 403) return 'This authenticated session does not have owner permission to adopt retained USD CPI history.';
  if (status === 404) return 'Retained USD CPI adoption is awaiting backend activation.';
  if (status === 409) return `Retained USD CPI adoption is withheld.${suffix}`;
  if (status === 503) return `The resident adoption mechanism is unavailable.${suffix}`;
  if (axios.isAxiosError(error) && !error.response) return `No backend response was received for retained USD CPI adoption.${suffix}`;
  return `Retained USD CPI adoption ${submission ? 'acknowledgement could not be confirmed' : 'status is unavailable'}.${suffix}`;
}
